import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button } from '@mui/material'
import { Drawer, CustomSwitch } from '../../../components'
import useColumns from '../../../hooks/useColumns'
import { PLANS } from '../../../utils/planNames'
import { useTranslation } from "react-i18next"

const SwitchesContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const ColumnDrawer = ({
  open,
  onClose,
  onColumnSave,
  COLUMNS,
  planName,
  withPendingIntake,
}) => {
  const initialState = {
    [COLUMNS.EMPLOYEES]: true,
    [COLUMNS.HIGH_FATIGUE]: true,
    [COLUMNS.NOTIFICATION]: true,
    [COLUMNS.COUNTER_MEASURE]: true,
    [COLUMNS.FATIGUE_LEVEL_8H]: true,
    [COLUMNS.INTAKE]: true,
  }

  const [columns, setColumns] = useState(initialState)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [id, setId] = useState(null)
  const { getColumns, saveColumns } = useColumns()
  const { t } = useTranslation('common')

  const handleColumnSelect = ({ columnName }) => {
    const column = columns[columnName]

    setColumns(prev => {
      return {
        ...prev,
        [columnName]: column ? false : true,
      }
    })

    return
  }

  const columnSelected = ({ columnName }) => {
    return columns[columnName] ? true : false
  }

  const onSave = async () => {
    setSaving(true)
    onColumnSave(columns)

    await saveColumns({
      id,
      highFatigue: columns.highFatigue,
      notification: columns.notification,
      counterMeasure: columns.counterMeasure,
      fatigueLevel8h: columns.fatigueLevel8h,
      intakePending: columns.intakePending,
    })

    setSaving(false)
    onClose()
  }

  const onReset = () => setColumns(initialState)

  useEffect(() => {
    const fetchColumns = async () => {
      const { data } = await getColumns()

      setColumns({
        [COLUMNS.EMPLOYEES]: data.employees,
        [COLUMNS.HIGH_FATIGUE]: data.highFatigue,
        [COLUMNS.NOTIFICATION]: data.notification,
        [COLUMNS.COUNTER_MEASURE]: data.counterMeasure,
        [COLUMNS.FATIGUE_LEVEL_8H]: data.fatigueLevel8h,
        [COLUMNS.INTAKE]: data.intakePending,
      })
      setId(data.id)

      setLoading(false)
    }

    if (open) {
      setLoading(true)
      fetchColumns()
    }
  }, [open])

  return (
    <Drawer
      title={t('columns.title')}
      open={open}
      onClose={onClose}
    >
      <Drawer.Body loading={loading}>
        <SwitchesContainer>
          <SwitchContainer>
            <span>{t('columns.colaborator')}</span>
            <CustomSwitch
              disabled
              isChecked={columnSelected({ columnName: COLUMNS.EMPLOYEES })}
            />
          </SwitchContainer>
          {withPendingIntake && (
            <SwitchContainer>
              <span>{t('columns.pending_intake')}</span>
              <CustomSwitch
                isChecked={columnSelected({ columnName: COLUMNS.INTAKE })}
                onClick={() =>
                  handleColumnSelect({ columnName: COLUMNS.INTAKE })
                }
              />
            </SwitchContainer>
          )}
          <SwitchContainer>
            <span>{t('columns.high_fatigue')}</span>
            <CustomSwitch
              isChecked={columnSelected({ columnName: COLUMNS.HIGH_FATIGUE })}
              onClick={() =>
                handleColumnSelect({ columnName: COLUMNS.HIGH_FATIGUE })
              }
            />
          </SwitchContainer>
          <SwitchContainer>
            <span>{t('columns.notification')}</span>
            <CustomSwitch
              isChecked={columnSelected({ columnName: COLUMNS.NOTIFICATION })}
              onClick={() =>
                handleColumnSelect({ columnName: COLUMNS.NOTIFICATION })
              }
            />
          </SwitchContainer>
          {planName === PLANS.OPTIMIZED && (
            <SwitchContainer>
              <span>{t('columns.counter_measure')}</span>
              <CustomSwitch
                isChecked={columnSelected({
                  columnName: COLUMNS.COUNTER_MEASURE,
                })}
                onClick={() =>
                  handleColumnSelect({ columnName: COLUMNS.COUNTER_MEASURE })
                }
              />
            </SwitchContainer>
          )}
          <SwitchContainer>
            <span>
              {planName !== PLANS.NOW
                ?  t('columns.alert_level') + '+8h'
                : t('columns.alert_index')}
            </span>
            <CustomSwitch
              isChecked={columnSelected({
                columnName: COLUMNS.FATIGUE_LEVEL_8H,
              })}
              onClick={() =>
                handleColumnSelect({ columnName: COLUMNS.FATIGUE_LEVEL_8H })
              }
            />
          </SwitchContainer>
        </SwitchesContainer>
      </Drawer.Body>
      <Drawer.Actions>
        <Button onClick={onReset}>{t('restore')}</Button>
        <Button
          onClick={onSave}
          variant='contained'
          disabled={loading || saving}
        >
          {t('save')}
        </Button>
      </Drawer.Actions>
    </Drawer>
  )
}
